import { FC, Fragment, useCallback, useState } from 'react';
import {
    ActionMenu,
    BodyText,
    Card,
    Divider,
    Eyebrow,
    Headline,
    Icon,
    Layout,
    Popover,
    Stack,
    Tag,
} from '@servicetitan/design-system';
import { Link, useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { useDependencies } from '@servicetitan/react-ioc';

import { EHModule } from '../../api/eh.api';

import { Logo } from '../logo/logo';
import * as Styles from './header.module.scss';
import { EHStore } from '../../stores/eh.store';
import { EHModulesHeaderList } from '../eh-modules-header-list';
import { GlobalSearch } from '../../../global-search';

interface HeaderProps {
    module?: EHModule;
}

export const Header: FC<HeaderProps> = observer(({ module }) => {
    const [{ username, isAdmin, isImpersonated, logout, routeContentModules }] =
        useDependencies(EHStore);

    const [menuOpen, setMenuOpen] = useState(false);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const history = useHistory();

    const handleToggleMenu = useCallback(() => {
        setMenuOpen(!menuOpen);
    }, [menuOpen]);

    const handleCloseMenu = useCallback(() => {
        setMenuOpen(false);
    }, []);

    const handleToggleAccountMenu = useCallback(() => {
        setAccountMenuOpen(!accountMenuOpen);
    }, [accountMenuOpen]);

    const handleCloseAccountMenu = useCallback(() => {
        setAccountMenuOpen(false);
    }, []);

    const handleRedirect2AccountSettings = useCallback(() => {
        history.push('/account-settings');
    }, [history]);

    return (
        <header className={Styles.header}>
            <div className={Styles.left}>
                <Logo to="/" />
                <Stack alignItems="center" className="m-l-2">
                    {(isAdmin || isImpersonated) && (
                        <Stack.Item>
                            <Tag color="#ffffff">Admin</Tag>
                        </Stack.Item>
                    )}
                    {isImpersonated && (
                        <Fragment>
                            <Stack.Item>
                                <Divider className="h-100" vertical spacing={1} />
                            </Stack.Item>
                            <Stack.Item>
                                <BodyText className="c-white">
                                    Impersonating {username?.toUpperCase()}
                                </BodyText>
                            </Stack.Item>
                        </Fragment>
                    )}
                </Stack>
            </div>

            <div className={Styles.center}>
                <GlobalSearch />
            </div>

            <div className={Styles.right}>
                {module && (
                    <Popover
                        trigger={
                            <Stack
                                onClick={handleToggleMenu}
                                alignItems="center"
                                className="c-white cursor-pointer"
                            >
                                <Stack.Item>
                                    <Divider className="c-neutral-90" vertical />
                                </Stack.Item>
                                <Stack.Item className="m-x-2">
                                    <Icon name="apps" />
                                </Stack.Item>
                                <Headline className="c-white m-b-0" size="small">
                                    {module.name}
                                </Headline>
                                <Stack.Item className="m-l-2">
                                    <Icon
                                        name={
                                            menuOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                                        }
                                    />
                                </Stack.Item>
                                <Stack.Item className="m-x-2">
                                    <Divider className="c-neutral-90" vertical />
                                </Stack.Item>
                            </Stack>
                        }
                        open={menuOpen}
                        direction="bl"
                        width="s"
                        sharp
                        padding="s"
                        onClickOutside={handleCloseMenu}
                    >
                        <Divider spacing="2" alignContent="start">
                            <Eyebrow className="m-r-2">Enterprise Hub</Eyebrow>
                        </Divider>

                        <Layout type="2Col" justifyContent="left">
                            {routeContentModules.map(m => (
                                <Layout.Section
                                    key={`header-route-content-module-${m.id}`}
                                    className="flex-basis-0-i flex-grow-0"
                                >
                                    <Link className="c-inherit" to={m.path}>
                                        <Card
                                            hoverable
                                            elevation="raised"
                                            active={module.id === m.id}
                                        >
                                            <Card.Section className="p-y-3-i p-x-2-i ta-center">
                                                <Icon
                                                    size="24"
                                                    className={classnames(
                                                        'c-neutral-300 border-radius-circular p-half',
                                                        m.iconBgColor,
                                                    )}
                                                    name={m.icon}
                                                />
                                                <BodyText
                                                    className="m-t-2 t-truncate"
                                                    bold
                                                    size="xsmall"
                                                >
                                                    {m.name}
                                                </BodyText>
                                            </Card.Section>
                                        </Card>
                                    </Link>
                                </Layout.Section>
                            ))}
                        </Layout>
                    </Popover>
                )}

                <EHModulesHeaderList />

                <ActionMenu
                    sharp
                    width="s"
                    offset={{ distance: 20 }}
                    direction="bl"
                    padding="s"
                    open={accountMenuOpen}
                    trigger={
                        <Icon
                            onClick={handleToggleAccountMenu}
                            size="24"
                            className="cursor-pointer"
                            name="account_circle"
                            color="#ffffff"
                        />
                    }
                    onClickOutside={handleCloseAccountMenu}
                    portal
                >
                    {username && (
                        <ActionMenu.Item disabled className="t-uppercase">
                            <Eyebrow>{username}</Eyebrow>
                        </ActionMenu.Item>
                    )}
                    <ActionMenu.Item onClick={handleRedirect2AccountSettings}>
                        Account Settings
                    </ActionMenu.Item>

                    <ActionMenu.Group>
                        <ActionMenu.Item onClick={logout}>
                            <Stack>Sign Out</Stack>
                        </ActionMenu.Item>
                    </ActionMenu.Group>
                </ActionMenu>
            </div>
        </header>
    );
});
